import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, PhoneCall, Mail, Clock, MapPin, CalendarCheck, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { CALENDLY_URL } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { name: "", phone: "", email: "", city: "", pest: "", message: "" };

const CONTACT_INFO = [
    { icon: Phone, label: "Téléphone", value: "02 55 99 22 43", href: "tel:+33255992243", testid: "contact-phone-link" },
    { icon: Mail, label: "Email", value: "contact@eradikbreizh.fr", href: "mailto:contact@eradikbreizh.fr", testid: "contact-email-link" },
    { icon: Clock, label: "Horaires", value: "Lundi - Dimanche, 9h00 à 20h00", testid: "contact-hours" },
    { icon: MapPin, label: "Zone d'intervention", value: "Basé au Mont-Dol (35) · Saint-Malo & rayon de 80 km", testid: "contact-zone" },
];

const Contact = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);

    const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/devis`, form);
            toast.success("Demande envoyée ! Nous vous rappelons au plus vite (7j/7, 9h-20h).");
            setForm(EMPTY_FORM);
        } catch (err) {
            toast.error("Une erreur est survenue. Appelez-nous directement au 02 55 99 22 43.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <Seo
                title="Contact & devis gratuit | Eradikbreizh"
                description="Contactez Eradikbreizh pour un devis gratuit : punaises de lit et cafards en Bretagne. Intervention sous 2h, 7j/7 de 9h à 20h au 02 55 99 22 43."
            />
            <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-24 pt-40 md:pb-32 md:pt-48">
                <div className="absolute -right-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <MaskedLine delay={0.1}><SectionTag>Contact & devis gratuit</SectionTag></MaskedLine>
                    <h1 className="mt-6 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl">
                        <MaskedLine delay={0.25}>Parlons de</MaskedLine>
                        <MaskedLine delay={0.4}>
                            <span className="text-brand">votre problème</span>
                        </MaskedLine>
                    </h1>
                    <MaskedLine delay={0.55}>
                        <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                            Décrivez-nous votre situation : nous vous rappelons rapidement pour un pré-diagnostic
                            et un devis gratuit. Intervention possible sous 2h, 7j/7.
                        </p>
                    </MaskedLine>

                    <div className="mt-16 grid gap-12 lg:grid-cols-5">
                        {/* INFOS */}
                        <div className="space-y-4 lg:col-span-2">
                            {CONTACT_INFO.map((info, i) => (
                                <Reveal key={info.label} delay={0.08 * i}>
                                    <div className="flex items-start gap-4 rounded-3xl border border-black/10 bg-white p-6 shadow-sm transition-colors duration-500 hover:border-brand/40">
                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                            <info.icon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{info.label}</p>
                                            {info.href ? (
                                                <a data-testid={info.testid} href={info.href} className="mt-1 block font-display text-lg font-bold transition-colors hover:text-brand">
                                                    {info.value}
                                                </a>
                                            ) : (
                                                <p data-testid={info.testid} className="mt-1 font-display text-lg font-bold">{info.value}</p>
                                            )}
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                            <Reveal delay={0.35}>
                                <a
                                    data-testid="contact-calendly-button"
                                    href={CALENDLY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between gap-4 rounded-3xl border border-brand/30 bg-brand/5 p-6 transition-[background-color,transform] duration-300 hover:bg-brand/10 hover:scale-[1.02]"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-white">
                                            <CalendarCheck className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <p className="font-display text-lg font-bold">Réservez en ligne en 2 min</p>
                                            <p className="text-xs uppercase tracking-widest text-slate-500">Choisissez votre créneau directement</p>
                                        </div>
                                    </div>
                                </a>
                            </Reveal>
                            <Reveal delay={0.4}>
                                <div className="overflow-hidden rounded-3xl border border-black/10 bg-white p-6 shadow-[0_20px_60px_rgba(10,15,36,0.12)]">
                                    <img
                                        data-testid="contact-technicien-photo"
                                        src="/assets/technicien-van.webp"
                                        alt="Votre technicien Eradikbreizh et son véhicule d'intervention"
                                        className="w-full"
                                    />
                                    <p className="mt-4 text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Votre interlocuteur unique, 7j/7</p>
                                </div>
                            </Reveal>
                        </div>

                        {/* FORM */}
                        <Reveal delay={0.15} className="lg:col-span-3">
                            <form
                                data-testid="devis-form"
                                onSubmit={onSubmit}
                                className="rounded-3xl border border-black/10 bg-white p-8 shadow-[0_20px_60px_rgba(10,15,36,0.08)] sm:p-10"
                            >
                                <h2 className="font-display text-2xl font-black uppercase tracking-tight">Demande de devis</h2>
                                <p className="mt-2 text-sm text-slate-500">Réponse rapide garantie, devis gratuit et sans engagement.</p>

                                <div data-testid="contact-urgence-notice" className="mt-6 rounded-2xl border border-brand/30 bg-brand/5 p-5">
                                    <div className="flex items-start gap-3">
                                        <PhoneCall className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                                        <div>
                                            <p className="font-display text-base font-black uppercase tracking-tight text-night">Urgence ou rendez-vous rapide ?</p>
                                            <p className="mt-1 text-sm leading-relaxed text-slate-600">
                                                Privilégiez l’appel téléphonique ou la réservation directe en ligne : votre créneau est confirmé immédiatement, sans attendre notre rappel.
                                            </p>
                                            <div className="mt-4 flex flex-wrap gap-3">
                                                <a
                                                    href="tel:+33255992243"
                                                    data-testid="contact-urgence-phone"
                                                    className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                                                >
                                                    <PhoneCall className="h-4 w-4" />
                                                    02 55 99 22 43
                                                </a>
                                                <a
                                                    href={CALENDLY_URL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid="contact-urgence-booking"
                                                    className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-white px-5 py-2.5 text-sm font-bold text-brand transition-[background-color,transform] duration-300 hover:bg-brand/10 hover:scale-105 active:scale-95"
                                                >
                                                    <CalendarCheck className="h-4 w-4" />
                                                    Réserver mon créneau
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="devis-name">Nom complet *</Label>
                                        <Input
                                            id="devis-name"
                                            data-testid="devis-name-input"
                                            required
                                            value={form.name}
                                            onChange={update("name")}
                                            placeholder="Jean Dupont"
                                            className="border-black/15 bg-slate-50 py-6 text-night placeholder:text-slate-400 focus-visible:ring-brand"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="devis-phone">Téléphone *</Label>
                                        <Input
                                            id="devis-phone"
                                            data-testid="devis-phone-input"
                                            required
                                            type="tel"
                                            value={form.phone}
                                            onChange={update("phone")}
                                            placeholder="06 12 34 56 78"
                                            className="border-black/15 bg-slate-50 py-6 text-night placeholder:text-slate-400 focus-visible:ring-brand"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="devis-email">Email *</Label>
                                        <Input
                                            id="devis-email"
                                            data-testid="devis-email-input"
                                            required
                                            type="email"
                                            value={form.email}
                                            onChange={update("email")}
                                            placeholder="jean@exemple.fr"
                                            className="border-black/15 bg-slate-50 py-6 text-night placeholder:text-slate-400 focus-visible:ring-brand"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="devis-city">Ville *</Label>
                                        <Input
                                            id="devis-city"
                                            data-testid="devis-city-input"
                                            required
                                            value={form.city}
                                            onChange={update("city")}
                                            placeholder="Saint-Malo"
                                            className="border-black/15 bg-slate-50 py-6 text-night placeholder:text-slate-400 focus-visible:ring-brand"
                                        />
                                    </div>
                                    <div className="space-y-2 sm:col-span-2">
                                        <Label>Type de nuisible *</Label>
                                        <Select
                                            required
                                            value={form.pest}
                                            onValueChange={(v) => setForm({ ...form, pest: v })}
                                        >
                                            <SelectTrigger data-testid="devis-pest-select" className="border-black/15 bg-slate-50 py-6 text-night focus:ring-brand">
                                                <SelectValue placeholder="Sélectionnez le nuisible" />
                                            </SelectTrigger>
                                            <SelectContent className="border-black/10 bg-white text-night">
                                                <SelectItem data-testid="devis-pest-option-punaises" value="punaises-de-lit">Punaises de lit</SelectItem>
                                                <SelectItem data-testid="devis-pest-option-cafards" value="cafards">Cafards / blattes</SelectItem>
                                                <SelectItem data-testid="devis-pest-option-inconnu" value="je-ne-sais-pas">Je ne sais pas</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2 sm:col-span-2">
                                        <Label htmlFor="devis-message">Décrivez votre situation</Label>
                                        <Textarea
                                            id="devis-message"
                                            data-testid="devis-message-input"
                                            value={form.message}
                                            onChange={update("message")}
                                            placeholder="Depuis quand ? Quelles pièces sont touchées ? Avez-vous déjà tenté un traitement ?"
                                            rows={5}
                                            className="border-black/15 bg-slate-50 text-night placeholder:text-slate-400 focus-visible:ring-brand"
                                        />
                                    </div>
                                </div>

                                <button
                                    data-testid="devis-submit-button"
                                    type="submit"
                                    disabled={loading}
                                    className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform,opacity] duration-300 hover:bg-brand-hover hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="h-5 w-5 animate-spin" />
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        <>
                                            Envoyer ma demande
                                            <Send className="h-5 w-5" />
                                        </>
                                    )}
                                </button>
                                <p className="mt-5 text-xs leading-relaxed text-slate-500">
                                    En envoyant ce formulaire, vous acceptez que vos données (nom, téléphone, email, ville) soient utilisées par Eradikbreizh uniquement pour traiter votre demande. Votre numéro de téléphone nous permet de vous joindre, par exemple pour vous envoyer un SMS de rappel de rendez-vous. Elles ne sont jamais partagées avec des tiers. Vous pouvez à tout moment vous inscrire gratuitement sur la liste d’opposition au démarchage téléphonique BLOCTEL. Pour exercer vos droits d’accès, de rectification ou de suppression, écrivez à contact@eradikbreizh.fr.{" "}
                                    <Link to="/politique-confidentialite" className="font-semibold text-brand underline">en savoir plus</Link>.
                                </p>
                            </form>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
